/* eslint-disable @typescript-eslint/indent */
import React, { useEffect, useRef, useState } from 'react';
import { useIntl, getLocale } from 'umi';
import {
  Bubble,
  ConfigProvider,
  Card,
  Row,
  Col,
  Table,
  Pagination,
} from 'tea-component';
import en_US from 'tea-component/es/i18n/locale/en_US';
import zh_CN from 'tea-component/es/i18n/locale/zh_CN';
import {
  DEFAULT_PAGING,
  PAGING_HEIGHT,
  TBODY_HEIGHT,
  THEAD_HEIGHT,
} from './config/constants';
import {
  MetricsTableColumn,
  PerformanceMetricsTableProps,
  SortBy,
} from './config/type';
import SvgIcon from '@/pages/home/components/SvgIcon';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import DownloadFormatModal from '@/pages/dashboard/components/DownloadFormatModal';
import SortPopover from './components/SortPopover';
import { handleTableCopyColumns } from '../../utils/tableCopy';
import { useOverLimitTip } from '@/pages/dashboard/components/OverLimitTip';
import { OverLimitInlineTip, OverLimitBar } from '@/pages/dashboard/components/OverLimitTip';

import '@/pages/dashboard/assets/scss/page/Overlay/components/tableConext.scss';
import '@/pages/dashboard/components/OverLimitTip/index.scss';
import classNames from 'classnames';
import { filterable } from 'tea-component/es/table/addons/filterable'; // fix filterable白屏问题
import CommonBubble from '@home/components/CommonBubble';
import './index.scss';

export * from './config/type';
const {
  stylize,
  columnsResizable,
  sortable,
  injectable,
  columnsDraggable,
  expandable,
} = Table.addons;

function createTheadBubble(
  columns: MetricsTableColumn<any>[],
  sortObject: any,
  copyObject: any,
  getRealRecord,
) {
  const { isNewSort, sorting, getRealSort } = sortObject || {};
  const handleTableCopyFunc = (type: string, key: string) => {
    const realRecord = getRealRecord();
    handleTableCopyColumns({
      type,
      key,
      columns,
      ...copyObject,
      records: realRecord,
    });
  };
  // eslint-disable-next-line semi-spacing
  for (let i = columns?.length - 1; i >= 0; i--) {
    const col: any = columns[i];
    if (typeof col.header === 'object') continue;
    const explain = col?.explain
      ? col?.explain?.replaceAll('\\n', '<br/>')
      : null;
    col.header = isNewSort ? (
      <SortPopover
        col={col}
        copyTitleList={copyObject?.copyTitleList}
        handleTableCopy={handleTableCopyFunc}
        sorting={sorting}
        getRealSort={getRealSort}
        value={col?.header}
      />
    ) : (
      <Bubble
        dark
        arrowPointAtCenter
        content={
          explain && <div dangerouslySetInnerHTML={{ __html: explain }} />
        }
      >
        <span style={{ cursor: explain ? 'help' : 'pointer' }}>
          {col.header}
        </span>
      </Bubble>
    );
  }
}

export default function PerformanceMetricsTable(
  props: PerformanceMetricsTableProps,
) {
  const locale = getLocale();
  const { formatMessage } = useIntl();
  const {
    title,
    secTitle,
    timeType,
    isNewSort = true,
    loading = true,
    isError = false,
    errorText = formatMessage({ id: 'common_loading_failed' }),
    height,
    topTip,
    showDownloadBtn = true,
    onDownload,
    columns,
    records = [],
    recordKey,
    onResizeEnd,
    sorting,
    paging,
    expand,
    filterConfig,
    selectType = '',
    // hasInjectableCell = true,
    isShowProgressBar,
    isHighlightSatSunDay,
    downloadConfig,
    className,
    bordered = false,
    copyConfig, // 复制相关配置，timetye直接用组件自带的就行
    defaultProgressVisible = true,
    onLockChange,
    hasLockColumns,
    beforeTableSolt,
    frontLogicalFlag,
    limitCount,
    barLimitCount,
    barDownloadDisabled = true,
    overLimitBarTriggerKey,
    overLimitTipFormatRule,
    ...attrs
  } = props;
  // copyDataType: revenue页面，不能直接复用selectType，传了selecttype的时候，排序异常
  const {
    copyTitleList,
    copyData,
    copyDataType,
    isHaveWeek = true,
  } = copyConfig || {};
  const defaultSorts = sorting?.defaultSorts;
  const recordCount = paging?.recordCount ?? 0;
  // const timeType = downloadConfig?.timeType;
  /* hooks */
  const [sorts, setSorts] = useState<SortBy[]>(defaultSorts || []);
  const [progressVisible, setProgressVisible] = useState(
    defaultProgressVisible,
  );
  const recordsRef = useRef(records);
  const sortRef = useRef(sorting);

  useEffect(() => {
    recordsRef.current = copyData ? copyData : records;
    sortRef.current = sorting;
  }, [records, copyData, sorting]);

  // 下载配置弹窗
  const [visible, setVisible] = useState(false);

  const [barDismissed, setBarDismissed] = useState(false);
  useEffect(() => {
    setBarDismissed(false);
  }, [overLimitBarTriggerKey]);
  // 兜底：未传 overLimitBarTriggerKey 的旧宽表组件，当 frontLogicalFlag 变为 has_more 时重置关闭状态
  useEffect(() => {
    if (frontLogicalFlag === 'has_more') {
      setBarDismissed(false);
    }
  }, [frontLogicalFlag]);

  useEffect(() => setSorts(defaultSorts || []), [defaultSorts]);
  const getRealRecord = () => recordsRef?.current;
  const getRealSort = () => sortRef?.current;
  records.length > 0 &&
    createTheadBubble(
      columns,
      { isNewSort, sorting, getRealSort },
      {
        records: copyData ? copyData : records,
        selectType: copyDataType ? copyDataType : selectType,
        formatMessage,
        timeType,
        copyTitleList,
        isHaveWeek,
      },
      getRealRecord,
    );
  let configPging;
  if (recordCount > 10) {
    configPging = {
      ...DEFAULT_PAGING,
      stateTextVisible: true,
      pageSizeVisible: true,
      pageIndexVisible: true,
      jumpVisible: true,
      endJumpVisible: true,
    };
  } else {
    configPging = { ...DEFAULT_PAGING };
  }

  const defaultAddons: any[] = [];
  {
    // 拖动调整列宽
    !!onResizeEnd &&
      defaultAddons.push(
        columnsResizable({ onResizeEnd, minWidth: 130, maxWidth: 500 }),
      );

    !!props.onDragEnd &&
      defaultAddons.push(columnsDraggable({ onDragEnd: props.onDragEnd }));

    // 表格内容区域高度
    defaultAddons.push(stylize({ bodyStyle: { maxHeight: height } }));

    // 单表头过滤
    filterConfig && defaultAddons.push(filterable(filterConfig));

    // 排序 - Payment的宽表不需要加selectType
    sorting &&
      defaultAddons.push(
        sortable({
          columns: columns
            ?.filter(({ header, sorter = true }) => !!header && sorter)
            ?.map((e) => ({ key: e.key, prefer: 'desc', sorter: () => 0 })),
          value:
            sorting?.config?.value ??
            sorts?.map((s) => {
              if (
                ![
                  'date',
                  'contrast_date',
                  'region',
                  'country',
                  'system',
                  'channel',
                  'language',
                  'product',
                  'category',
                  'bundle',
                  'zone',
                  'event_name',
                  // 竞品
                  'release_time',
                  'game_name',
                  // registerFunnel注册漏斗
                  'app_version',
                  'network_type',
                  'memory',
                  'store_channel',
                  // 夜莺时间对比
                  'churn_level',
                  'event_type',
                  'event',
                  // dl2
                  'title',
                  'price',
                  // 核心数据汇总
                  'wow_top_modid',
                  'wow_top_playercnt',
                  'wow_top_modname',
                  'wow_top_uid',
                  'wow_top_vrolename',
                  // poe
                  'game_stage',
                  'character_class',
                  'user_type',
                  // 新增维度
                  'ua_network',
                  'version',
                  'time',
                  "campaign",
                  "ad_format",
                  "pv_threshold",
                  "catalog"
                ].includes(s?.by)
              ) {
                ['value_only', 'dod', 'sdlw', 'change'].forEach((v) => {
                  s.by = s?.by?.replace(v, ''); // 先去掉原有的type
                });
                s.by = s?.by + selectType; // 再加上type
              }

              return {
                ...s,
                order: s?.order?.startsWith('asc') ? 'asc' : s?.order?.startsWith('desc') ? 'desc' : s?.order,
              };
            }),
          onChange: (value, context) => {
            const sortList = value.length ? [context.sort] : []; // 限制为单列排序
            if (!isNewSort) {
              setSorts(sortList);
              sorting?.onSortChange && sorting.onSortChange(sortList, context);
            }
          },
        }),
      );

    // 行展开（分版本宽表）
    expand &&
      defaultAddons.push(
        expandable({
          expandedKeys: expand?.expandedKeys,
          onExpandedKeysChange: (keys, { event }) => {
            event.stopPropagation();
            expand?.onExpandedKeysChange(keys, { event });
          },
          expand: (record) => record.child,
          rowExpand: true,
          shouldRecordExpandable: (record) => record?.child?.length > 0,
        }),
      );

    // 有进度条下单元格注入类名
    defaultAddons.push(
      injectable({
        cell: (props, ctx) => ({
            className:
              ctx.column.isPercent && progressVisible
                ? `is-percent header-click-${ctx?.column?.key} head-${ctx.column?.index % 2 ? 'even' : 'odd'
                }`
                : `is-no-percent header-click-${ctx?.column?.key} head-${ctx.column?.index % 2 ? 'even' : 'odd'
                }`,
            // className: ctx.column.isPercent && progressVisible ? 'table-text-bg' : '',
          }),
        row: (props, context) => ({
            className:
              isHighlightSatSunDay && context?.record?.isSatOrSun
                ? 'isSatSunDay'
                : expand && !context?.record?.child
                  ? 'dbd-version-table-bg'
                  : '',
          }),
      }),
    );
  }

  const recordKeyVal = recordKey ? { recordKey } : {};

  // 解决点击锁图标时，table加载导致浮窗会闪弹到左上角问题
  useEffect(() => {
    if (hasLockColumns) {
      const nodeList = document.querySelectorAll('.tea-overlay');
      if (nodeList?.length > 0 && loading) {
        nodeList.forEach(ele => ele.style.display = 'none');
      }
    }
  }, [loading]);

  const clickLock = (e: React.SyntheticEvent<HTMLSpanElement>, item: MetricsTableColumn) => {
    e.stopPropagation();
    e.preventDefault();
    onLockChange?.(item);
  };

  // 增加锁列排序功能
  let newColumns = columns;
  if (hasLockColumns) {
    newColumns = columns?.map(item => {
      const { hasLock, lock, header, lockTip, unlockTip } = item;
      if (hasLock) {
        return {
          ...item,
          header: (
            <div className='dbd-performance-table-header-lock-row'>
              {typeof header === 'function' ? header(item) : header}
              <CommonBubble content={lock ? lockTip : unlockTip} updateDeps={[loading]}>
                <div className='dbd-performance-table-header-icon' onClick={(e) => clickLock(e, item)}>
                  <SvgIcon className={lock ? 'dbd-performance-table-header-lock' : 'dbd-performance-table-header-unlock'}
                    type={lock ? 'lock-icon' : 'lock-open'} />
                </div>
              </CommonBubble>
            </div>)
        };
      }
      return item;
    });
  };

  const handleDownloadTrigger = () => {
    if (downloadConfig && !downloadConfig.isOnlyDown) {
      setVisible(true);
    } else {
      onDownload?.();
    }
  };

  const { tipVisible } = useOverLimitTip(frontLogicalFlag);

  const barVisible = tipVisible && !barDismissed;

  const PreTable = (
    <>
      {beforeTableSolt}
      <Table
        className={classNames(className, 'dbd-performance-table', { 'dbd-table-not-copy': !isNewSort })}
        bordered={'all'} // TODO：只用于新宽表样式，原来的不需要加，需要判断下
        {...{
          columns: !recordCount ? [] : newColumns,
          // records: sorting
          //   ? [...records].sort(sortable.comparer(sorts))
          //   : records,
          records,
          addons: defaultAddons,
          ...attrs,
        }}
        {...recordKeyVal}
      />
      {recordCount > 10 && (
          <ConfigProvider locale={locale === 'en-US' ? en_US : zh_CN}>
          <Pagination
            recordCount={recordCount}
            stateText={
              recordCount > 10 ? (
                <div className="dbd-pagination-state">
                  <span className="dbd-pagination-state__records">
                    {`${recordCount} ${formatMessage({ id: 'common_records' })}`}
                  </span>
                  {tipVisible && (
                    <>
                      <span className="dbd-pagination-state__sep">|</span>
                      <OverLimitInlineTip
                        className="dbd-pagination-state__tip"
                        limitCount={limitCount}
                        overLimitTipFormatRule={overLimitTipFormatRule}
                        onDownload={handleDownloadTrigger}
                        linkClassName="dbd-pagination-state__tip-link"
                      />
                    </>
                  )}
                </div>
              ) : ''
            }
            {...configPging}
            {...paging}
          />
        </ConfigProvider>
      )}
      {/* <Pagination
      className='margin-t-20 page-r'
      total={recordCount}
      hideOnSinglePage={recordCount <= 10}
      showSizeChanger
      showTotal={total => total > 10 ? `${total} ${formatMessage({ id: 'common_records' })}` : ''}
      defaultPageSize={10}
      defaultCurrent={1}
      current={paging?.page}
      {...configPging}
      {...paging}
    /> */}
    </>
  );

  const triggerProgress = () => {
    setProgressVisible(!progressVisible);
  };

  // @ts-ignore
  return (
    <>
      <Card bordered={bordered} className={bordered ? '' : 'overall-conext'}>
        {barVisible && (
          <OverLimitBar
            limitCount={limitCount}
            barLimitCount={barLimitCount}
            overLimitTipFormatRule={overLimitTipFormatRule}
            barDownloadDisabled={barDownloadDisabled}
            onDownload={handleDownloadTrigger}
            onDismiss={() => setBarDismissed(true)}
          />
        )}
        <Card.Body
          title={<div className="overall-conext-title">{title}{secTitle && <div className='overall-conext-sectitle'>{secTitle}</div>}</div>}
          operation={
            recordCount ? (
              <div className="overall-conext-flex">
                {topTip?.right || null}
                {/* {
                  isShowProgressBar &&
                    <div className="db-PerformanceMetricsTable__trigger-progress overall-conext-item" onClick={triggerProgress}>
                      <SvgIcon className="icon-visible" type={progressVisible ? "visible" : "hidden"} />
                    </div>
                } */}
                {isShowProgressBar && (
                  <div
                    className="overall-conext-item"
                    onClick={triggerProgress}
                  >
                    <Bubble
                      dark
                      placement="top"
                      content={
                        !progressVisible
                          ? formatMessage({ id: 'overview_close_progress' })
                          : formatMessage({ id: 'overview_show_progress' })
                      }
                    >
                      <span>
                        <SvgIcon
                          className="overall-conext-icon"
                          type={progressVisible ? 'table-eye' : 'table-noeye'}
                        />
                      </span>
                    </Bubble>
                  </div>
                )}

                {showDownloadBtn ? (
                  <div
                    className="overall-conext-item"
                    onClick={() => {
                      if (downloadConfig && !downloadConfig.isOnlyDown) {
                        // 这个判断是为了兼容 下载配置弹窗写在业务组件中的逻辑
                        setVisible(!visible);
                      } else {
                        onDownload?.();
                      }
                    }}
                  >
                    <span onClick={() => { }}>
                      <SvgIcon
                        className="overall-conext-icon"
                        type="download"
                      />
                    </span>
                  </div>
                ) : (
                  ''
                )}
              </div>
            ) : null
          }
        >
          <>{topTip?.left || null}</>
          <Row>
            <Col>
              <Abnormal
                isLoading={false}
                componentLoading={loading}
                isError={isError}
                failedMsg={errorText}
                isNoData={!recordCount}
                component={PreTable}
                style={{
                  height: THEAD_HEIGHT + TBODY_HEIGHT + PAGING_HEIGHT + 1,
                }}
              />
            </Col>
          </Row>
          {showDownloadBtn && downloadConfig && (
            <DownloadFormatModal
              visible={visible}
              onCancel={() => setVisible(false)}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onConfirm={(value) => downloadConfig.onDownload?.(value)}
              defaultValue={downloadConfig?.defaultValue}
              metricOptions={downloadConfig.metricOptions}
              timeType={downloadConfig.timeType}
              showData={downloadConfig.showData || {}}
              tableName={downloadConfig.customDownName || title || ''}
              showModalData={downloadConfig.showModalData}
              showModalDataStat={downloadConfig.showModalDataStat}
              defaultFileType={downloadConfig.defaultFileType}
              customSelectorsDimensions={downloadConfig?.customSelectorsDimensions}
              defaultSelectorValues={downloadConfig?.defaultSelectorValues}
              requiredMetricCountToDownload={downloadConfig?.requiredMetricCountToDownload}
            />
          )}
        </Card.Body>
      </Card>
    </>
  );
}
